/**
 * Farm segment delta DIRECTIVES: what a stored delta segment cannot say on its own.
 *
 * Purpose: the delta reader (`applyFarmObjectDelta`) keeps the prior value for a null (see
 * removable-scalar-policy.ts for why), cannot remove a key, appends an item added to an id-keyed list
 * at the end, and matches the entries of an id-less list by `id`. A version that clears a value, drops
 * a key, inserts mid-list or changes an id-less list therefore read back wrong. The CFM GO import
 * (#37380) stores each GO version of an endorsed term as a delta and met all four in the CFM export
 * (a co-insured's cleared insurance score, liability dropped, a building inserted between two others,
 * an interested party's re-linked items).
 *
 * Flow: the writer (`calculateFarmSegmentDelta(prev, curr, { directives: true })`) compares the two
 * full states and records, on the delta segment's own `deltaDirectives`, exactly the paths the
 * ordinary delta cannot express. The reader (`makePolicyDetailsFromSegments`) applies a segment's
 * directives after its delta.
 *
 * Key decisions:
 *  - Backward compatible by construction: nothing reinterprets null or order in a delta. Only a
 *    segment that carries `deltaDirectives` behaves differently, and no segment carried it before.
 *  - Opt-in on the writer: policy-mod's endorsement deltas are unchanged (staff clearing a value is
 *    still limited to removable-scalar-policy.ts; a follow-up could opt policy-mod in).
 *  - Paths address list items by id (`[id=<id>]`), never by position, so a directive still lands on the
 *    right item when an earlier segment is revised. An order directive lists ids only: locations and
 *    risks are extracted to one extended-data row per id, so the delta never carries an item twice.
 *  - An id-less list read entry by entry is set whole; such lists are small (an interested party's
 *    linked items).
 *  - A path whose item no longer exists is skipped, like the reader's unmatched tombstones (#34355).
 */

import cloneDeep from 'lodash.clonedeep';

import { getArrayMergePolicy } from './array-merge-policy';
import { isWholeValueField, SEGMENT_RATING_SNAPSHOT_FIELD_NAMES } from './whole-value-policy';

/** The segment field the directives live on. Not a GraphQL type field: staff-fe reads it as opaque JSON. */
export const FARM_DELTA_DIRECTIVES_FIELD = 'deltaDirectives';

/** A path step: an object key, or `[id=<id>]` for the item of a list with that id. */
export type FarmDeltaPath = string[];

export interface FarmDeltaDirectives {
  /** Values to put in place after the delta: an explicit null, or an id-less list whole. */
  set?: Array<{ path: FarmDeltaPath; value: unknown }>;
  /** Keys the version no longer has. */
  unset?: FarmDeltaPath[];
  /** The id order of an id-keyed list after the delta. */
  order?: Array<{ path: FarmDeltaPath; ids: string[] }>;
}

const ITEM_STEP = /^\[id=(.*)\]$/;
const itemStep = (id: unknown): string => `[id=${String(id)}]`;

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * A segment's own bookkeeping, not policy state: the reader keeps the first segment's `segmentType` and
 * takes the rest from each delta as it comes, so a later segment without one is not a removal.
 */
const SEGMENT_BOOKKEEPING_FIELDS = new Set([
  'segmentId',
  'segmentType',
  'segmentNumber',
  'startDate',
  'endDate',
  'segmentDays',
  'dailyRate',
  'premium',
  'segmentPremium',
  'premiumBreakdown',
  'segmentPremiumBreakdown',
  'recalculate'
]);

/** Every element an object with an id (an empty list counts): the lists the reader merges by id. */
const isIdKeyedList = (list: unknown[]): boolean => list.every((item) => isPlainObject(item) && item.id != null);

/** The list read entry by entry by `id` (the default read), as opposed to a list read whole or by another key. */
const readsById = (key: string): boolean => getArrayMergePolicy(key).read.kind === 'legacy-id';

/**
 * The directives that make `curr` read back from `prev` plus the ordinary delta between them. Empty when
 * the ordinary delta is enough.
 */
export function farmDeltaDirectives(prev: unknown, curr: unknown): FarmDeltaDirectives {
  const directives: Required<FarmDeltaDirectives> = { set: [], unset: [], order: [] };

  const walkObject = (before: Record<string, any>, after: Record<string, any>, path: FarmDeltaPath): void => {
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
      if (
        UNSAFE_KEYS.has(key) ||
        key === FARM_DELTA_DIRECTIVES_FIELD ||
        (path.length === 0 &&
          (SEGMENT_BOOKKEEPING_FIELDS.has(key) || SEGMENT_RATING_SNAPSHOT_FIELD_NAMES.includes(key)))
      ) {
        continue;
      }
      const was = before[key];
      const now = after[key];
      if (!(key in after)) {
        if (was !== undefined) {
          directives.unset.push([...path, key]);
        }
      } else if (now === null) {
        if (was !== null) {
          directives.set.push({ path: [...path, key], value: null });
        }
      } else if (isWholeValueField(key)) {
        // Carried whole by the delta whenever it changes.
      } else if (Array.isArray(was) && Array.isArray(now)) {
        walkList(key, was, now, [...path, key]);
      } else if (isPlainObject(was) && isPlainObject(now)) {
        walkObject(was, now, [...path, key]);
      }
    }
  };

  const walkList = (key: string, before: unknown[], after: unknown[], path: FarmDeltaPath): void => {
    if (!readsById(key)) {
      // A list read whole, or keyed by another field, is carried complete by the delta when it changes.
      return;
    }
    if (!isIdKeyedList(before) || !isIdKeyedList(after)) {
      if (JSON.stringify(before) !== JSON.stringify(after)) {
        directives.set.push({ path, value: cloneDeep(after) });
      }
      return;
    }
    const beforeById = new Map((before as Array<Record<string, any>>).map((item) => [item.id, item]));
    const afterIds = (after as Array<Record<string, any>>).map((item) => item.id);
    for (const item of after as Array<Record<string, any>>) {
      const prior = beforeById.get(item.id);
      if (prior) {
        walkObject(prior, item, [...path, itemStep(item.id)]);
      }
    }
    // The reader keeps the surviving items in place and appends the added ones in order.
    const afterIdSet = new Set(afterIds);
    const readOrder = [
      ...(before as Array<Record<string, any>>).map((item) => item.id).filter((id) => afterIdSet.has(id)),
      ...afterIds.filter((id) => !beforeById.has(id))
    ];
    if (readOrder.some((id, index) => id !== afterIds[index])) {
      directives.order.push({ path, ids: afterIds.map(String) });
    }
  };

  if (isPlainObject(prev) && isPlainObject(curr)) {
    walkObject(prev, curr, []);
  }
  return Object.fromEntries(Object.entries(directives).filter(([, list]) => list.length > 0));
}

/** The object or list at `path`, or undefined when a step is missing. */
function resolve(root: unknown, path: FarmDeltaPath): unknown {
  let node: unknown = root;
  for (const step of path) {
    if (UNSAFE_KEYS.has(step)) {
      return undefined;
    }
    const item = ITEM_STEP.exec(step);
    if (item) {
      node = Array.isArray(node)
        ? node.find((entry) => isPlainObject(entry) && String(entry.id) === item[1])
        : undefined;
    } else {
      node = isPlainObject(node) ? node[step] : undefined;
    }
    if (node === undefined) {
      return undefined;
    }
  }
  return node;
}

const isPath = (path: unknown): path is FarmDeltaPath =>
  Array.isArray(path) && path.length > 0 && path.every((step) => typeof step === 'string');

/** The directives, or a throw naming what is wrong: a directive the reader cannot follow must stop the read. */
function validFarmDeltaDirectives(directives: unknown): FarmDeltaDirectives {
  const refuse = (problem: string): never => {
    throw new Error(`Cannot read segment deltaDirectives: ${problem}`);
  };
  if (!isPlainObject(directives)) {
    return refuse('not an object');
  }
  for (const key of Object.keys(directives)) {
    if (key !== 'set' && key !== 'unset' && key !== 'order') {
      refuse(`unknown directive "${key}"`);
    }
  }
  const { set, unset, order } = directives;
  if (
    set !== undefined &&
    !(Array.isArray(set) && set.every((entry) => isPlainObject(entry) && isPath(entry.path) && 'value' in entry))
  ) {
    refuse('`set` must be a list of { path, value }');
  }
  if (unset !== undefined && !(Array.isArray(unset) && unset.every(isPath))) {
    refuse('`unset` must be a list of paths');
  }
  if (
    order !== undefined &&
    !(
      Array.isArray(order) &&
      order.every(
        (entry) =>
          isPlainObject(entry) &&
          isPath(entry.path) &&
          Array.isArray(entry.ids) &&
          entry.ids.every((id: unknown) => typeof id === 'string')
      )
    )
  ) {
    refuse('`order` must be a list of { path, ids }');
  }
  return directives as FarmDeltaDirectives;
}

/**
 * Apply a segment's directives to the details its delta was just applied to. Staff-fe receives the field as
 * an AWSJSON string and passes store segments straight to the reader, so a string is parsed here; one that
 * does not parse throws rather than reading the segment without its directives.
 */
export function applyFarmDeltaDirectives(
  details: Record<string, any>,
  raw: FarmDeltaDirectives | string | null | undefined
): void {
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      throw new Error(`Cannot read segment deltaDirectives: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  if (parsed === null || parsed === undefined) {
    return;
  }
  const directives = validFarmDeltaDirectives(parsed);
  for (const path of directives.unset ?? []) {
    const parent = resolve(details, path.slice(0, -1));
    const key = path[path.length - 1];
    if (isPlainObject(parent) && !UNSAFE_KEYS.has(key)) {
      delete parent[key];
    }
  }
  for (const { path, value } of directives.set ?? []) {
    const parent = resolve(details, path.slice(0, -1));
    const key = path[path.length - 1];
    if (isPlainObject(parent) && !UNSAFE_KEYS.has(key)) {
      parent[key] = cloneDeep(value);
    }
  }
  for (const { path, ids } of directives.order ?? []) {
    const list = resolve(details, path);
    if (!Array.isArray(list)) {
      continue;
    }
    const rank = new Map(ids.map((id, index) => [id, index]));
    // Items the order does not name keep their relative place after the named ones.
    const ranked = list.map((item, index) => ({ item, index, rank: rank.get(String(item?.id)) ?? ids.length + index }));
    ranked.sort((a, b) => a.rank - b.rank);
    list.splice(0, list.length, ...ranked.map(({ item }) => item));
  }
}
